import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FaUser,
  FaUserDoctor,
  FaRightToBracket,
  FaUserPlus,
  FaArrowRightFromBracket,
  FaChevronDown,
  FaUserInjured,
} from "react-icons/fa6";

import { logoutUser } from "../../redux/slices/authSlice";
import { clearAllNotifications } from "../../redux/slices/notificationSlice";

import SearchBar from "../../Components/SearchBar/SearchBar";

import { getProfileImageUrl } from "../../Components/utils/imageUtils";
import { getBusinessUserProfileImageUrl } from "../../Components/utils/buserimageUtils";

// ============================================================
// GRADIENTS
// ============================================================

const BUTTON_GRADIENT =
  "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))";

const HEADER_GRADIENT =
  "linear-gradient(135deg, rgba(14,165,233,0.18) 0%, rgba(255,255,255,0.92) 50%, rgba(30,73,118,0.16) 100%)";

// ============================================================
// ROUTES
// IMPORTANT: These must exactly match your App.jsx routes
// ============================================================

const ROUTES = {
  HOME: "/Home",
  LOGIN: "/login",
  PATIENT_SIGNUP: "/sign-up",
  DOCTOR_SIGNUP: "/RegistrationForm",
  USER_PROFILE: "/dashboard/user",
  BUSINESS_PROFILE: "/dashboard/business-user",
};

// ============================================================
// HEADER
// ============================================================

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // =========================================================
  // STATE
  // =========================================================

  const [imageError, setImageError] = useState(false);
  const [showSignupMenu, setShowSignupMenu] = useState(false);

  // =========================================================
  // AUTH
  // =========================================================

  const { user, token } = useSelector((state) => state.auth);

  const isLoggedIn = Boolean(user && token);

  // =========================================================
  // BUSINESS
  // =========================================================

  const isBusiness = user?.type === "business" || user?.role === "business";

  // =========================================================
  // PROFILE IMAGE
  // =========================================================

  const imageUrl = isBusiness
    ? getBusinessUserProfileImageUrl(user?.profilePic)
    : getProfileImageUrl(user?.profilePic);

  // =========================================================
  // RESET IMAGE ERROR
  // =========================================================

  useEffect(() => {
    setImageError(false);
  }, [user?.profilePic]);

  // =========================================================
  // CLOSE SIGNUP MENU
  // =========================================================

  useEffect(() => {
    setShowSignupMenu(false);
  }, [isLoggedIn]);

  // =========================================================
  // HOME
  // =========================================================

  const goHome = () => {
    setShowSignupMenu(false);
    navigate(ROUTES.HOME);
  };

  // =========================================================
  // PATIENT SIGNUP
  // =========================================================

  const goToPatientSignup = () => {
    setShowSignupMenu(false);

    navigate(ROUTES.PATIENT_SIGNUP);
  };

  // =========================================================
  // DOCTOR SIGNUP
  // =========================================================

  const goToDoctorSignup = () => {
    setShowSignupMenu(false);

    navigate(ROUTES.DOCTOR_SIGNUP);
  };

  // =========================================================
  // PROFILE
  // =========================================================

  const handleProfileClick = () => {
    setShowSignupMenu(false);

    if (isBusiness) {
      navigate(ROUTES.BUSINESS_PROFILE);
    } else {
      navigate(ROUTES.USER_PROFILE);
    }
  };

  // =========================================================
  // LOGOUT
  // =========================================================

  const handleLogout = async () => {
    setShowSignupMenu(false);

    try {
      await dispatch(logoutUser()).unwrap();

      dispatch(clearAllNotifications());

      navigate(ROUTES.LOGIN, {
        replace: true,
      });
    } catch (error) {
      console.error("Logout error:", error);

      navigate(ROUTES.LOGIN, {
        replace: true,
      });
    }
  };

  // =========================================================
  // HEADER
  // =========================================================

  return (
    <header
      className="
        sticky
        top-0
        z-50
        w-full
        overflow-visible
        shadow-md
      "
    >
      {/* =====================================================
          TOP QUOTE
      ===================================================== */}

      <div className="w-full bg-slate-950">
        <div
          className="
            mx-auto
            w-full
            max-w-[1700px]
            px-2
            sm:px-4
            md:px-6
            lg:px-8
          "
        >
          <div
            className="
              flex
              min-h-[22px]
              items-center
              justify-center

              sm:min-h-[28px]
            "
          >
            <div
              className="
                flex
                w-full
                min-w-0
                items-center
                gap-2
                sm:gap-4
              "
            >
              {/* LEFT LINE */}

              <span
                className="
                  hidden
                  h-px
                  flex-1
                  bg-cyan-400/50
                  sm:block
                "
              />

              {/* QUOTE */}

              <p
                className="
                  min-w-0
                  flex-1
                  text-center
                  text-[8px]
                  font-medium
                  leading-4
                  tracking-wide
                  text-cyan-200

                  sm:flex-none
                  sm:text-[11px]

                  md:text-xs
                "
              >
                <span className="hidden sm:inline">
                  वैद्यो नारायणो हरिः
                  {" — "}
                </span>

                <span>“The healer is a form of God.”</span>
              </p>

              {/* RIGHT LINE */}

              <span
                className="
                  hidden
                  h-px
                  flex-1
                  bg-cyan-400/50
                  sm:block
                "
              />
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          MAIN HEADER
      ===================================================== */}

      <div
        className="
          w-full
          border-b
          border-blue-100
        "
        style={{
          background: HEADER_GRADIENT,
        }}
      >
        <div
          className="
            mx-auto
            w-full
            max-w-[1700px]
            px-2
            py-1.5

            sm:px-4
            sm:py-2

            md:px-6

            lg:px-8
          "
        >
          {/* =================================================
              HEADER WRAPPER
          ================================================= */}

          <div
            className="
              flex
              w-full
              min-w-0
              flex-col
              gap-1.5

              sm:gap-2
            "
          >
            {/* =================================================
                TOP ROW

                MOBILE:
                LOGO + ACTIONS

                DESKTOP:
                LOGO + SEARCH + ACTIONS
            ================================================= */}

            <div
              className="
                flex
                w-full
                min-w-0
                items-center
                gap-1.5

                sm:gap-3
                md:gap-4
              "
            >
              {/* =================================================
                  LOGO
              ================================================= */}

              <button
                type="button"
                onClick={goHome}
                aria-label="Go to MR Health home"
                className="
                  flex
                  min-w-0
                  shrink-0
                  items-center
                  focus:outline-none
                "
              >
                <img
                  src="/logo.jpeg"
                  alt="MR Health"
                  title="MR Health"
                  className="
                    block
                    h-11
                    w-auto
                    max-w-[115px]
                    object-contain

                    sm:h-14
                    sm:max-w-[140px]

                    md:h-16
                    md:max-w-[160px]

                    lg:h-20
                    lg:max-w-[190px]
                  "
                />
              </button>

              {/* =================================================
                  DESKTOP SEARCH
              ================================================= */}

              <div
                className="
                  hidden
                  min-w-0
                  flex-1
                  lg:block
                "
              >
                <div
                  className="
                    mx-auto
                    w-full
                    max-w-3xl
                  "
                >
                  <SearchBar />
                </div>
              </div>

              {/* =================================================
                  RIGHT ACTIONS
              ================================================= */}

              <div
                className="
                  ml-auto
                  flex
                  shrink-0
                  items-center
                  justify-end
                  gap-1

                  sm:gap-2

                  md:gap-2.5
                "
              >
                {/* =================================================
                    LOGGED IN
                ================================================= */}

                {isLoggedIn ? (
                  <>
                    {/* PROFILE IMAGE */}

                    <button
                      type="button"
                      onClick={handleProfileClick}
                      aria-label="Open profile"
                      className="
                        flex
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        focus:outline-none
                      "
                    >
                      {imageUrl && !imageError ? (
                        <img
                          src={imageUrl}
                          alt="Profile"
                          className="
                            h-8
                            w-8
                            rounded-full
                            border-2
                            border-white
                            object-cover
                            shadow-md

                            sm:h-10
                            sm:w-10

                            md:h-11
                            md:w-11

                            lg:h-12
                            lg:w-12
                          "
                          onError={() => setImageError(true)}
                        />
                      ) : (
                        <div
                          className="
                            flex
                            h-8
                            w-8
                            items-center
                            justify-center
                            rounded-full
                            bg-white
                            shadow-md

                            sm:h-10
                            sm:w-10

                            md:h-11
                            md:w-11

                            lg:h-12
                            lg:w-12
                          "
                        >
                          {isBusiness ? (
                            <FaUserDoctor
                              className="
                                text-sm
                                text-blue-600

                                sm:text-base
                                md:text-lg
                              "
                            />
                          ) : (
                            <FaUser
                              className="
                                text-sm
                                text-blue-600

                                sm:text-base
                                md:text-lg
                              "
                            />
                          )}
                        </div>
                      )}
                    </button>

                    {/* PROFILE */}

                    <button
                      type="button"
                      onClick={handleProfileClick}
                      className="
                        hidden
                        flex-col
                        items-center
                        justify-center
                        text-blue-950
                        transition
                        hover:text-sky-600
                        focus:outline-none

                        sm:flex
                      "
                    >
                      <span
                        className="
                          flex
                          h-8
                          w-8
                          items-center
                          justify-center
                          rounded-full
                          bg-blue-950/10

                          md:h-9
                          md:w-9
                        "
                      >
                        {isBusiness ? (
                          <FaUserDoctor size={13} />
                        ) : (
                          <FaUser size={13} />
                        )}
                      </span>

                      <span
                        className="
                          mt-0.5
                          text-[8px]
                          font-semibold

                          md:text-[10px]
                        "
                      >
                        Profile
                      </span>
                    </button>

                    {/* LOGOUT */}

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="
                        flex
                        h-8
                        shrink-0
                        items-center
                        justify-center
                        gap-1
                        rounded-lg
                        bg-red-600
                        px-2
                        text-[9px]
                        font-semibold
                        text-white
                        shadow-md
                        transition
                        hover:bg-red-500
                        active:scale-95
                        focus:outline-none

                        sm:h-9
                        sm:px-3
                        sm:text-[10px]

                        md:text-xs
                      "
                    >
                      <FaArrowRightFromBracket />

                      <span className="hidden sm:inline">Logout</span>

                      <span className="sm:hidden">Exit</span>
                    </button>
                  </>
                ) : (
                  /* =================================================
                     LOGGED OUT
                  ================================================= */

                  <div
                    className="
                      flex
                      shrink-0
                      items-center
                      gap-1

                      sm:gap-2
                    "
                  >
                    {/* =================================================
                        LOGIN
                    ================================================= */}

                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.LOGIN)}
                      className="
                        flex
                        h-8
                        shrink-0
                        items-center
                        justify-center
                        gap-1
                        rounded-lg
                        border
                        border-blue-200
                        bg-white
                        px-2
                        text-[8px]
                        font-semibold
                        text-blue-950
                        shadow-sm
                        transition
                        hover:bg-blue-50
                        hover:text-sky-600
                        active:scale-95
                        focus:outline-none

                        sm:h-9
                        sm:px-3
                        sm:text-[10px]

                        md:text-xs
                      "
                    >
                      <FaRightToBracket />

                      <span>Login</span>
                    </button>

                    {/* =================================================
                        SIGN UP DROPDOWN
                    ================================================= */}

                    <div
                      className="
                        relative
                        shrink-0
                      "
                    >
                      <button
                        type="button"
                        onClick={() => setShowSignupMenu((prev) => !prev)}
                        aria-expanded={showSignupMenu}
                        aria-haspopup="menu"
                        className="
                          flex
                          h-8
                          shrink-0
                          items-center
                          justify-center
                          gap-1
                          rounded-lg
                          px-2
                          text-[8px]
                          font-semibold
                          text-white
                          shadow-md
                          transition
                          hover:opacity-90
                          active:scale-95
                          focus:outline-none

                          sm:h-9
                          sm:px-3
                          sm:text-[10px]

                          md:text-xs
                        "
                        style={{
                          background: BUTTON_GRADIENT,
                        }}
                      >
                        <FaUserPlus />

                        <span>Sign Up</span>

                        <FaChevronDown
                          className={`
                            text-[7px]
                            transition-transform

                            ${showSignupMenu ? "rotate-180" : ""}
                          `}
                        />
                      </button>

                      {/* =================================================
                          SIGNUP MENU
                      ================================================= */}

                      {showSignupMenu && (
                        <div
                          className="
                            absolute
                            right-0
                            top-full
                            z-[9999]
                            mt-2

                            w-[260px]
                            max-w-[calc(100vw-16px)]

                            overflow-hidden
                            rounded-2xl
                            border
                            border-sky-100
                            bg-white
                            shadow-2xl
                          "
                        >
                          {/* PATIENT */}

                          <button
                            type="button"
                            onClick={goToPatientSignup}
                            className="
                              flex
                              w-full
                              items-center
                              gap-3
                              px-4
                              py-4
                              text-left
                              transition
                              hover:bg-sky-50
                              focus:outline-none
                            "
                          >
                            <div
                              className="
                                flex
                                h-9
                                w-9
                                shrink-0
                                items-center
                                justify-center
                                rounded-lg
                                text-white
                              "
                              style={{
                                background: BUTTON_GRADIENT,
                              }}
                            >
                              <FaUserInjured />
                            </div>

                            <div className="min-w-0">
                              <p
                                className="
                                  text-sm
                                  font-bold
                                  text-gray-800
                                "
                              >
                                Patient Sign Up
                              </p>

                              <p
                                className="
                                  text-xs
                                  text-gray-500
                                "
                              >
                                Create patient account
                              </p>
                            </div>
                          </button>

                          {/* DIVIDER */}

                          <div
                            className="
                              mx-4
                              border-t
                              border-gray-100
                            "
                          />

                          {/* DOCTOR */}

                          <button
                            type="button"
                            onClick={goToDoctorSignup}
                            className="
                              flex
                              w-full
                              items-center
                              gap-3
                              px-4
                              py-4
                              text-left
                              transition
                              hover:bg-sky-50
                              focus:outline-none
                            "
                          >
                            <div
                              className="
                                flex
                                h-9
                                w-9
                                shrink-0
                                items-center
                                justify-center
                                rounded-lg
                                bg-emerald-100
                                text-emerald-600
                              "
                            >
                              <FaUserDoctor />
                            </div>

                            <div className="min-w-0">
                              <p
                                className="
                                  text-sm
                                  font-bold
                                  text-gray-800
                                "
                              >
                                Doctor / Professional
                              </p>

                              <p
                                className="
                                  text-xs
                                  text-gray-500
                                "
                              >
                                Register healthcare service
                              </p>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* =================================================
                MOBILE SEARCH
            ================================================= */}

            <div
              className="
                block
                w-full
                min-w-0
                lg:hidden
              "
            >
              <div
                className="
                  w-full
                  min-w-0
                "
              >
                <SearchBar />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
